import { DO_NOT_USE_OR_YOU_WILL_BE_FIRED_EXPERIMENTAL_FORM_ACTIONS } from "react";
import { convertFileSrc } from "@tauri-apps/api/core";
import { TigerViewElement } from "./TigerViewPanel";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import type { ParsedGraphParams } from "../utils/graphParams";
import { parseGraphParams } from "../utils/graphParams";
import { parseMultiTimeMode } from "../utils/buildMultiTimeGraphParams";
import { resolveXLabelType, formatXAxisLabel, xAxisTickSpacePx } from "../utils/graphXLabel";
import { maxPlotDurationSec, formatMultiTimeAxisLabel } from "../utils/multiTimeAxis";
import { computeWidgetTooltipPosition } from "../utils/tooltipPosition";
import { getTigerResource } from "../utils/tigerResource";

// Exact f64 representation of Rust's f32::MAX — matches values arriving from the backend.
const INVALID_TIGER_VALUE = 3.4028234663852886e+38;

export interface ItemValue {
    id: number;
    t: string;
    v: number[];
    c: string;
}

export interface GraphSeriesData {
    label: string;
    color: string;
    timestamps: number[];
    values: number[];
    // Populated only for XY plots (graph_type=6); when present, the x-axis comes
    // from this channel instead of `timestamps`.
    x_values?: number[];
    // XY plots only: the `plot[i].show_last_value` ini flag.
    show_last_value?: boolean;
}

export interface GraphValue {
    id: string;
    m_graph_data: GraphSeriesData[];
}

function get_tiger_colour( colour: string) : string
{
    return colour;
}

// Escape text that is spliced into an innerHTML SVG string so view-file labels
// containing &, <, or > cannot break the markup.
function escapeXmlText( text: string ) : string
{
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// Tracks the global Live/Replay switch so the STATUS widget can label the
// status line with the current run state, mirroring the C++ dv_run_state that
// drives tiger_status_widget::get_mode_string(). TigerViewPanel keeps this in
// sync via the view-mode-changed event.
let widgetRunStateLive = true;
export function setWidgetRunStateLive(live: boolean): void {
    widgetRunStateLive = live;
}

function get_session_type() : string
{
    // The main Tiger UI is always live or replay. (The C++ #ifdef ALARM_SERVER
    // build omits the mode label; that path isn't reachable here, but the
    // STATUS ALARM branch is kept for parity.)
    return widgetRunStateLive ? "LIVE" : "REPLAY";
}

function get_tiger_theme() : string
{
    return "";
}

// Per-element flag used to bind tooltip listeners exactly once. Most widget
// handlers reassign `el.innerHTML` every tick; binding each tick (the original
// behaviour) leaked closures and grew WebView2's heap linearly. The flag keeps
// the listener count at one per element regardless of how often TOOLTIP() runs.
const TOOLTIP_BOUND_FLAG = '__tigerTooltipBound';
// Per-element flag holding the latest tooltip text, refreshed every tick. The
// delayed `mouseenter` reveal (and live updates while hovering) reads it, so a
// widget whose value changes after binding still shows the current value.
const TOOLTIP_TEXT_FLAG = '__tigerTooltipText';

// A single shared tooltip node, reused by every widget and parented to
// <body> rather than the widget. Living outside the view panel means its
// `overflow: hidden` can't truncate the tooltip when a widget sits at a border
// (the original bug), and a per-tick `el.innerHTML` rewrite can't wipe it.
// position:fixed + viewport clamping mirrors GraphTooltip.
let sharedTooltip: HTMLDivElement | null = null;
// The widget the shared tooltip is currently shown for, so live value updates
// only reposition while that same widget is still hovered.
let tooltipOwner: HTMLElement | null = null;

function getSharedTooltip(): HTMLDivElement {
    if (!sharedTooltip) {
        sharedTooltip = document.createElement('div');
        sharedTooltip.className = 'tooltip-box';
        document.body.appendChild(sharedTooltip);
    }
    return sharedTooltip;
}

function showTooltip(el: HTMLElement, text: string) {
    const tip = getSharedTooltip();
    tooltipOwner = el;
    tip.innerText = text;
    // Make it measurable (display:block) before reading its size; the browser
    // only paints after this synchronous block, so setting coordinates below
    // avoids a flash at the previous position.
    tip.classList.add('visible');
    const rect = el.getBoundingClientRect();
    const { left, top } = computeWidgetTooltipPosition(
        rect, tip.offsetWidth, tip.offsetHeight, window.innerWidth, window.innerHeight,
    );
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
}

function hideTooltip(el: HTMLElement) {
    // A newer hover may already own the shared tooltip; don't hide it then.
    if (tooltipOwner !== el) return;
    tooltipOwner = null;
    if (sharedTooltip) sharedTooltip.classList.remove('visible');
}

// Dismiss the shared tooltip unconditionally. The owning widget can be
// unmounted mid-hover (e.g. switching views without moving the mouse), and its
// `mouseleave` then never fires; the view calls this on layout change so the
// body-level tooltip can't linger over the new content.
export function hideWidgetTooltip(): void {
    tooltipOwner = null;
    if (sharedTooltip) sharedTooltip.classList.remove('visible');
}

function TOOLTIP( el : HTMLElement, ttValue: string )
{
    const elFlags = el as unknown as Record<string, unknown>;
    elFlags[TOOLTIP_TEXT_FLAG] = ttValue;

    // Keep the visible tooltip in sync (text + position, since the live value
    // can change its width) while this widget is the hovered one.
    if (tooltipOwner === el) showTooltip(el, ttValue);

    if (elFlags[TOOLTIP_BOUND_FLAG]) return;
    elFlags[TOOLTIP_BOUND_FLAG] = true;

    let delay: ReturnType<typeof setTimeout> | undefined;

    el.addEventListener('mouseenter', () => {
        delay = setTimeout(() => {
            showTooltip(el, String(elFlags[TOOLTIP_TEXT_FLAG] ?? ''));
        }, 500);
    });

    el.addEventListener('mouseleave', () => {
        if (delay !== undefined) clearTimeout(delay);
        hideTooltip(el);
    });
}

// The browser collapses literal spaces (U+0020) under `white-space: normal`,
// dropping the leading and repeated spaces that TEXT widget values can carry
// (e.g. a quoted " °C" label). Convert them to the non-breaking-space entity
// before assigning to innerHTML so the exact spacing survives, matching the
// `&nbsp;` substitution ANALOG_BOX / DIGITAL_BOX already do.
function preserve_spaces_html( text: string ): string
{
    return text.replace(/ /g, "&nbsp;");
}

function TEXT( tiger_el : TigerViewElement, itemValue: ItemValue )
{
    const el = document.getElementById(tiger_el.id);
    if (!el) return;

    el.innerHTML = preserve_spaces_html(itemValue.t);
}

function TEXT_COLOUR( tiger_el : TigerViewElement, itemValue: ItemValue )
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   el.innerHTML = preserve_spaces_html(itemValue.t);
   el.style.color = get_tiger_colour(itemValue.c);
}

function STATUS ( tiger_el : TigerViewElement, itemValue: ItemValue, ox: number, scale: number )
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const status_string = tiger_el.func_params ?? "";

   // Mode label prepended to the status line, mirroring the C++
   // tiger_status_widget::get_mode_string() (" Live " / "Replay ").
   let mode_prefix : string;
   if (get_session_type() == "ALARM") {
       mode_prefix = "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;";
   }
   else if (get_session_type() == "REPLAY") {
       mode_prefix = preserve_spaces_html(getTigerResource("TIGER_WIDGET_STATUS_OFFLINE"));
   }
   else {
       mode_prefix = preserve_spaces_html(getTigerResource("TIGER_WIDGET_STATUS_ONLINE"));
   }

   // The configured x anchors the turbine-name/time portion; the mode label
   // grows leftward from there (C++ get_data subtracts the prefix width from
   // time_x_pos) instead of pushing the time to the right. Measure the rendered
   // prefix and shift the widget left by it. Recomputing the base left from the
   // config each tick keeps this idempotent across the 1 s poll and resizes.
   el.innerHTML = "<span class=\"tiger-status-mode\">" + mode_prefix + "</span>" + status_string + itemValue.t;
   const mode_span = el.querySelector<HTMLSpanElement>(".tiger-status-mode");
   const prefix_width = mode_span ? mode_span.getBoundingClientRect().width : 0;
   el.style.left = Math.round(ox + tiger_el.x * scale - prefix_width) + "px";
}

function ANALOG( tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;










































































    const params = (tiger_el.func_params ?? "").split('|');
   const tag_name : string = params[0];
   const description : string = params[1];

    el.innerHTML = itemValue.t;
    el.style.color = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);
}

function MAINTENANCE_TEXT( tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    el.innerHTML = itemValue.t;
    el.style.color = itemValue.c;

    TOOLTIP(el, itemValue.t);
}

function MAINTENANCE_DATE(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    el.innerHTML = itemValue.t;
    el.style.color = itemValue.c;

 //    TOOLTIP(itemtt, tiger_el.func_params + ': ' + itemValue.t);
}

function ANALOG_BOX(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const box_descr : string = params[0];
   const box_unit : string = params[1];
   const tag_name : string = params[2];
   const description : string = params[3];

    var colour_array = itemValue.c.split('|');

    const reg = new RegExp(" ", "g");
    const descr_html = box_descr.replace(reg, '&nbsp;');
    const value_html = itemValue.t.replace(reg, '&nbsp;');
    const unit_html = box_unit != '' ? '&nbsp;' + box_unit.replace(reg, '&nbsp;') : '';

    el.innerHTML =
        '<span style="color:' + colour_array[2] + '">' + descr_html + '</span>' +
        '<span style="color:' + colour_array[1] + '">' + value_html + '</span>' +
        '<span style="color:' + colour_array[2] + '">' + unit_html + '</span>';

    el.style.backgroundColor = colour_array[0];

     TOOLTIP(el, tag_name + ' ' + itemValue.t + '  ' + description);
}

function DIGITAL_BOX(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

  let buffer = itemValue.t;

    const reg = new RegExp(" ", "g");
    buffer = buffer.replace(reg, '&nbsp;');
    el.innerHTML = buffer;
    el.style.color = itemValue.c;

 const params = (tiger_el.func_params ?? "").split('|');
   const tag_name : string = params[0];
   const description : string = params[1];


    TOOLTIP(el, params[0] + ' ' + itemValue.t + getTigerResource("TIGER_WIDGET_TOOLTIP_DIGITAL") + params[1]);
}


function DIGITAL( tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const tag_name : string = params[0];
   const description : string = params[1];

    el.innerHTML = itemValue.t;
    el.style.color = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);
}

function DIGITALSTATUS(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const tag_name : string = params[0];
   const description : string = params[1];

    el.innerHTML = itemValue.t;
    el.style.color = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);
}

function DIGITALTEXT(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const tag_name : string = params[0];
   const description : string = params[1];

       el.innerHTML = itemValue.t;
    el.style.color = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);
}

function ALARM_CONNECTION_BOX(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   // Framed box: border always drawn in the status colour; filled with the same
   // colour unless v[0] == 0 (Disconnected), which is drawn frame-only.
   const filled = (itemValue.v[0] ?? 0) !== 0;
   el.style.borderColor = itemValue.c;
   el.style.backgroundColor = filled ? itemValue.c : 'transparent';

   TOOLTIP(el, itemValue.t);
}

function RECTANGLE(tiger_el : TigerViewElement, itemValue: ItemValue)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    const params = (tiger_el.func_params ?? "").split('|');
   const tag_name : string = params[0];
   const description : string = params[1];

        el.style.backgroundColor = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);

}

function BARCHART(tiger_el : TigerViewElement, itemValue: ItemValue, oy: number, scaleY : number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const top_y : number = parseFloat(params[0]);
   const height : number = parseFloat(params[1]);
   const min : number = parseFloat(params[2]);
   const max : number = parseFloat(params[3]);
   const tag_name : string = params[4];
   const description : string = params[5];

	var current_value = itemValue.v[0];
    var current_height = 0;
    var current_top_y = top_y;

    if ( max >= min ) {
        if (itemValue.t == '####') {
            current_height = 0;
            current_top_y = top_y + height;
        }
        else if (current_value > max) {
            current_height = height;
            current_top_y = top_y;
        }
        else if ( current_value < min ) {
            current_height = 0;
            current_top_y = top_y + height;
        }
        else {
            current_height = Math.round( ( ( current_value - min ) / ( max - min ) ) * height );
            current_top_y = top_y + height - current_height;
        }
    }
    else {
        if (itemValue.t == '####') {
            current_height = 0;
            current_top_y = top_y;
        }
        else if ( current_value < max ) {
            current_height = height;
            current_top_y = top_y;
        }
        else if ( current_value > min ) {
            current_height = 0;
            current_top_y = top_y;
        }
        else {
            current_height = height - Math.round( ( ( current_value + max ) / ( max - min ) ) * height );
            current_top_y = top_y;
        }
    }

    current_top_y = oy + Math.round((current_top_y * scaleY));
    current_height = Math.round((current_height * scaleY));

    el.style.top = current_top_y + 'px';
    el.style.height = current_height + 'px';
     el.style.backgroundColor = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);
}

function HORIZ_BARCHART(tiger_el : TigerViewElement, itemValue: ItemValue, ox: number, scaleX: number) 
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const left : number = parseFloat(params[0]);
   const width : number = parseFloat(params[1]);
   const min : number = parseFloat(params[2]);
   const max : number = parseFloat(params[3]);
   const tag_name : string = params[4];
   const description : string = params[5];

	var current_value = itemValue.v[0];
    var current_width = 0;
    var current_left = left;

    if (itemValue.t == '####') {
        current_width = 0;
    }
    else if (current_value > max) {
        current_width = width;
    }
    else if (current_value < min) {
        current_width = 0;
    }
    else {
        current_width = Math.round(((current_value + min) / (max - min)) * width);
    }

    current_left = ox + Math.round(current_left * scaleX);
    current_width = Math.round(current_width * scaleX);

    el.style.left = current_left + 'px';
    el.style.width = current_width + 'px';

     el.style.backgroundColor = itemValue.c;

    TOOLTIP(el, tag_name + ' ' + itemValue.t + ' ' + description);
}

function STACK_BARCHART(tiger_el : TigerViewElement, itemValue: ItemValue, oy: number, scale : number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    while (el.firstChild)
        el.removeChild(el.firstChild);

   const params = (tiger_el.func_params ?? "").split('|');
   const left : number = parseFloat(params[0]);
   const top_y : number = parseFloat(params[1]);
   const width : number = parseFloat(params[2]);
   const height : number = parseFloat(params[3]);
   const min : number = parseFloat(params[4]);
   const max : number = parseFloat(params[5]);
   const tag_name : string = params[6];
   const description : string = params[7];
   const unit : string = params[8];

    var current_value = itemValue.v[0];
    var current_height = 0;
    var current_top_y = top_y;

    if (itemValue.t == '####') {
        current_height = 0;
        current_top_y = top_y + height;
    }
    current_height = height;
    current_top_y = top_y;

    el.style.top = Math.round(oy + (current_top_y * scale)) + 'px';
    el.style.height =  Math.round(current_height * scale) + 'px';

    var state_array = itemValue.t.split('|');
    var colour_array = itemValue.c.split('|');

    var number_of_states: number = colour_array.length;

    var stack_bar_html: string;

    stack_bar_html = "";
    var bar_height : number = 0;
    var bar_width :number = width;
    var bar_x: number = 0;
    var bar_y: number = height;

    var range: number = (max - min) / height;

    for (var array_index: number = 0; array_index < number_of_states; array_index++) {
        var min_value: number = parseFloat(state_array[array_index * 2]);
        var max_value: number = parseFloat(state_array[(array_index * 2) + 1]);
        var colour: string = colour_array[array_index];

        bar_height = Math.round((max_value - min_value) / range);

        bar_y = bar_y - bar_height;

        stack_bar_html += "<div class='TVBS' style=\"left:" + bar_x + "px;top:" + Math.round((bar_y * scale)) + "px;width:" + Math.round(bar_width * scale) + "px;height:" +
            + Math.round(bar_height * scale) + "px;background-color:" + colour +";\"></div>";
    }

    el.innerHTML = stack_bar_html;

    // itemValue.t here is a pipe-delimited list of state range pairs, not a
    // single readable value, so the tooltip carries the tag/description.
    // description already includes the unit as "[unit] descr" from the backend.
    TOOLTIP(el, tag_name + ' ' + description);
}

function EXHAUST_SPREAD(tiger_el : TigerViewElement, itemValue: ItemValue, ox: number, scaleX: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');

   const left : number = parseFloat(params[0]);
   const width : number = parseFloat(params[1]);
   const max_spread : number = parseFloat(params[2]);
   const tag_name : string = params[4];
   const description : string = params[5];

	var current_value = itemValue.v[0];
    var current_width = 0;
    var current_left = left;

    // Draw the bar only for a valid value. Invalid/no-data arrives either as the
    // '####' text sentinel (matching BARCHART/HORIZ_BARCHART) or the numeric
    // INVALID_TIGER_VALUE; in both cases leave current_width at 0 so nothing is
    // rendered (a stale v[0] would otherwise draw a full-width bar).
    if (itemValue.t != '####' && current_value != INVALID_TIGER_VALUE) {
        if ( current_value > max_spread ) {
        current_width = width;
        current_left = left;
        }
        else if ( current_value < -max_spread ) {
        current_width = width;
        current_left = left - width;
        }
        else if ( current_value >= 0 ) {
            current_width = Math.round( ( current_value / max_spread ) * width );
        current_left = left;
        }
        else {
            current_width = Math.round( ( ( current_value * -1.0 ) / max_spread ) * width );
        current_left = left - current_width;
        }
    }

    current_left = ox + Math.round(current_left * scaleX);
    current_width = Math.round(current_width * scaleX);

    el.style.left = current_left + 'px';
    el.style.width = current_width + 'px';

     el.style.backgroundColor = itemValue.c;

    TOOLTIP( el, getTigerResource("TIGER_WIDGET_SPREAD_FOR") + tag_name + ' ' + itemValue.t + ' ' + description );
}

// Source URL for a bitmap file under %TIGER% (DIGITAL_BITMAP's on/off images).
//
// Desktop: the Tauri asset protocol, via convertFileSrc. On the web there is no
// asset protocol — convertFileSrc dereferences `window.__TAURI_INTERNALS__`,
// which is undefined in a plain browser, so calling it threw a TypeError: the
// <img> was never built AND the exception escaped update_tiger_widgets, killing
// TigerViewPanel's forEach for every element after the bitmap. Serve the file
// through the webserver's image endpoint instead — the same one the view
// background picture uses (api_tiger_image: %TIGER%-scoped, BMP transcoded to
// PNG so browsers can display it). The response is cached for a day
// (Cache-Control: private, max-age=86400); the bytes for a path are stable.
function tiger_bitmap_src(path: string): string {
    if (typeof window !== "undefined" && "__TAURI_INTERNALS__" in window) {
        return convertFileSrc(path);
    }
    return `/api/tiger-image?path=${encodeURIComponent(path)}`;
}

function DIGITAL_BITMAP(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
    const el = document.getElementById(tiger_el.id);
    if (!el) return;

    const params = (tiger_el.func_params ?? "").split('|');
    const tag_name : string = params[0];
    const description : string = params[1];

    const scaledW = Math.round((tiger_el.width ?? 32) * scale);
    const scaledH = Math.round((tiger_el.height ?? 32) * scale);

    if (el.dataset.c != itemValue.c
        || el.dataset.scale !== String(scale)) {
        el.dataset.c = itemValue.c;
        el.dataset.scale = String(scale);

        el.innerHTML = `<img src="${tiger_bitmap_src(itemValue.c)}" width="${scaledW}" height="${scaledH}" style="display:block">`;
    }

    TOOLTIP(el, tag_name + ' ' + itemValue.t + getTigerResource("TIGER_WIDGET_TOOLTIP_DIGITAL") + description);
}

function CIRCLE(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const diameter : number = parseFloat(params[0]);
   const tag_name : string = params[1];
   const description : string = params[2];

    var radius = Math.round(diameter / 2.0);
    var r =  Math.round( radius * scale);
    var center = Math.round(( radius + 1) * scale);
    var dim = Math.round(((radius * 2) + 2) * scale);

    let circle = el.querySelector<SVGCircleElement>('circle');

    const fill = itemValue.c || 'none';
    if (!circle || el.dataset.scale !== String(scale)) {
        while (el.firstChild) el.removeChild(el.firstChild);
        var svg : string = `<svg style="overflow:visible" width="${dim}px" height="${dim}px">`;
        svg += `<circle cx="${center}px" cy="${center}px" r="${r}"`;
        svg += ` fill="${fill}"`;
        svg += ` stroke="${get_tiger_colour('black')}"`;
        svg += ` stroke-width="1"/>`;
        svg += `</svg>`;
        el.innerHTML = svg;
        el.dataset.scale = String(scale);
    } else {
        circle.setAttribute('fill', fill);
    }

    TOOLTIP( el, tag_name + ' ' + itemValue.t + ' ' + description );
}

function FLAME_INTENSITY(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const diameter : number = parseFloat(params[0]);
   const tag_name : string = params[1];
   const description : string = params[2];
   const text_string : string = params[3] ?? "";
   const text_colour : string = params[4] ?? "";

    // Size the SVG so the circle plus its 1px stroke always fits: deriving
    // `center`/`dim` from `r` (rather than rounding each independently) avoids the
    // off-by-one that clipped the circle's right/bottom stroke at some zooms.
    const strokeWidth = 1;
    const r = Math.max(1, Math.round((diameter / 2.0) * scale));
    const center = r + strokeWidth;
    const dim = r * 2 + strokeWidth * 2;

    // Low-intensity flames report the default white fill (backend maps "white" →
    // "#FFFFFF"). Mirror the C++ draw_framed_circle(fill_circle = colour !=
    // white_colour): leave the circle unfilled (transparent) so only the outline
    // shows and the background reads through.
    const c = itemValue.c;
    const isLow = !c || c === "none" || c.toLowerCase() === "#ffffff" || c.toLowerCase() === "white";
    const fill = isLow ? "none" : c;

    let circle = el.querySelector<SVGCircleElement>('circle');

    if (!circle || el.dataset.scale !== String(scale)) {
        while (el.firstChild) el.removeChild(el.firstChild);
        var svg : string = `<svg style="overflow:visible" width="${dim}px" height="${dim}px">`;
        svg += `<circle cx="${center}" cy="${center}" r="${r}"`;
        svg += ` fill="${fill}"`;
        svg += ` stroke="${get_tiger_colour('black')}"`;
        svg += ` stroke-width="${strokeWidth}"/>`;
        // Optional label (FLAME_INTENSITY <tag> <x> <y> <diameter> <text> <colour>)
        // drawn after the circle so it sits on top, in its associated colour.
        if (text_string) {
            const fontSize = Math.round(15 * scale);
            svg += `<text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central"`;
            svg += ` font-family="Courier New" font-size="${fontSize}px"`;
            svg += ` fill="${text_colour || get_tiger_colour('black')}">${escapeXmlText(text_string)}</text>`;
        }
        svg += `</svg>`;
        el.innerHTML = svg;
        el.dataset.scale = String(scale);
    } else {
        circle.setAttribute('fill', fill);
    }

    TOOLTIP( el, tag_name + ' ' + itemValue.t + ' ' + description );
}

function EXHAUST_PATTERN(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const diameter : number = parseFloat(params[0]);
   const tag_name : string = params[1];
   const description : string = params[2];

    var radius = Math.round(diameter / 2.0);
    var r =  Math.round( radius * scale);
    var center = Math.round(( radius + 1) * scale);
    var dim = Math.round(((radius * 2) + 2) * scale);

    let circle = el.querySelector<SVGCircleElement>('circle');

    if (!circle || el.dataset.scale !== String(scale)) {
        while (el.firstChild) el.removeChild(el.firstChild);
        var svg : string = `<svg style="overflow:visible" width="${dim}px" height="${dim}px">`;
        svg += `<circle cx="${center}px" cy="${center}px" r="${r}"`;
        svg += ` fill="${itemValue.c}"/>`;
        svg += `</svg>`;
        el.innerHTML = svg;
        el.dataset.scale = String(scale);
    } else {
        circle.setAttribute('fill', itemValue.c);
    }

    TOOLTIP( el, tag_name + ' ' + itemValue.t + getTigerResource("TIGER_WIDGET_TOOLTIP_DIGITAL") + description );
}

function createArc(
  diameter: number,
  startAngle: number,  // degrees, 0 = top, clockwise
  endAngle: number,    // degrees, 0 = top, clockwise
  arcWidth: number,
  scale: number,
): string {
  const toRad = (deg: number) => ((deg - 90) * Math.PI) / 180;

  // Normalize both angles to [0, 360)
  const normalize = (deg: number) => ((deg % 360) + 360) % 360;
  const s = normalize(startAngle);
  const e = normalize(endAngle);

  const centerX : number = (diameter + (2 * arcWidth)) / 2;
  const centerY : number = (diameter + (2 * arcWidth)) / 2;

  
  const outerR = diameter / 2;
  const innerR = outerR - arcWidth;
  const start = toRad(startAngle);
  const end = toRad(endAngle);

  const span = s <= e ? e - s : 360 - s + e;
  const largeArc = span > 180 ? 1 : 0;

  const ox1 = Math.round((centerX + outerR * Math.cos(start)) * scale);
  const oy1 = Math.round((centerY + outerR * Math.sin(start)) * scale);;
  const ox2 = Math.round((centerX + outerR * Math.cos(end)) * scale);;
  const oy2 = Math.round((centerY + outerR * Math.sin(end)) * scale);;
  const ix1 = Math.round((centerX + innerR * Math.cos(end)) * scale);;
  const iy1 = Math.round((centerY + innerR * Math.sin(end)) * scale);;
  const ix2 = Math.round((centerX + innerR * Math.cos(start)) * scale);;
  const iy2 = Math.round((centerY + innerR * Math.sin(start)) * scale);;


  return [
    `M ${ox1} ${oy1}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${ox2} ${oy2}`,  // sweep=1 → always clockwise
    `L ${ix1} ${iy1}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${ix2} ${iy2}`,  // sweep=0 → always counter-clockwise
    `Z`,
  ].join(" ");
}

function EXHAUST_ARC(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const diameter : number = parseFloat(params[0]);
   const start_angle : number = parseFloat(params[1]);
   const end_angle : number = parseFloat(params[2]);
   const arc_width : number = parseFloat(params[3]);

   const tag_name : string = params[4];
   const description : string = params[5];

    let pathEl = el.querySelector<SVGPathElement>('path');

    if (!pathEl || el.dataset.scale !== String(scale)) {
        while (el.firstChild) el.removeChild(el.firstChild);
        var dim = Math.round((diameter + arc_width) * scale);
        const path = createArc(diameter, start_angle, end_angle, arc_width, scale);

        var svg : string = `<svg style="overflow:visible" width="${dim}px" height="${dim}px">`;
        svg += `<path d="${path}" fill="${itemValue.c}"/>`;
        svg += `</svg>`;
        el.innerHTML = svg;
        el.dataset.scale = String(scale);
    } else {
        pathEl.setAttribute('fill', itemValue.c);
    }

    TOOLTIP( el, tag_name + ' ' + itemValue.t + description );
}

function TRIANGLE(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

   const params = (tiger_el.func_params ?? "").split('|');
   const x1 : number = parseFloat(params[0]);
   const y1 : number = parseFloat(params[1]);
   const x2 : number = parseFloat(params[2]);
   const y2 : number = parseFloat(params[3]);
   const x3 : number = parseFloat(params[4]);
   const y3 : number = parseFloat(params[5]);


   const tag_name : string = params[6];
   const description : string = params[7];

    const sx1 = Math.round(x1 * scale);
    const sy1 = Math.round(y1 * scale);
    const sx2 = Math.round(x2 * scale);
    const sy2 = Math.round(y2 * scale);
    const sx3 = Math.round(x3 * scale);
    const sy3 = Math.round(y3 * scale);

    const allX = [sx1, sx2, sx3];
    const allY = [sy1, sy2, sy3];
    const svgW = Math.max(...allX) + 2;
    const svgH = Math.max(...allY) + 2;

    const points = `${sx1},${sy1} ${sx2},${sy2} ${sx3},${sy3}`;


    let polygon = el.querySelector<SVGPolygonElement>('polygon');

    if (!polygon || el.dataset.scale !== String(scale)) {
        while (el.firstChild) el.removeChild(el.firstChild);
        var svg : string = `<svg style="overflow:visible" width="${svgW}px" height="${svgH}px">`;
        svg += `<polygon points="${points}" fill="${itemValue.c}" stroke="black" stroke-width="1"/>`;
        svg += `</svg>`;
        el.innerHTML = svg;
        el.dataset.scale = String(scale);
    } else {
        polygon.setAttribute('fill', itemValue.c);
    }

    TOOLTIP( el, tag_name + ' ' + itemValue.t + description );
}

function TRIANGLE_VALUE(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    while (el.firstChild)
        el.removeChild(el.firstChild);

   const params = (tiger_el.func_params ?? "").split('|');
   const width : number = parseFloat(params[0]);
   const height : number = parseFloat(params[1]);
   const min_value : number = parseFloat(params[2]);
   const max_value : number = parseFloat(params[3]);

   const tag_name : string = params[4];
   const description : string = params[5];

   	var current_value = itemValue.v[0];

        if (current_value < min_value) {
        current_value = min_value;
    }
    else if (current_value > max_value) {
        current_value = max_value;
    }

    var current_y: number = height - Math.round(((current_value - min_value) / (max_value - min_value)) * height);

    var x1:number = 0;
    var y1:number = current_y;
    var x2:number = x1;
    var y2:number = y1 - width;
    var x3:number = x2 + width;
    var y3: number = y2 + Math.round((width / 2));

    const sx1 = Math.round(x1 * scale);
    const sy1 = Math.round(y1 * scale);
    const sx2 = Math.round(x2 * scale);
    const sy2 = Math.round(y2 * scale);
    const sx3 = Math.round(x3 * scale);
    const sy3 = Math.round(y3 * scale);

    const allX = [sx1, sx2, sx3];
    const allY = [sy1, sy2, sy3];
    const svgW = Math.max(...allX) + 2;
    const svgH = Math.max(...allY) + 2;

    const points = `${sx1},${sy1} ${sx2},${sy2} ${sx3},${sy3}`;

    // Scale marks, drawn FIRST so the arrow overlays them: a 1px black
    // horizontal line as wide as the arrow at the height the arrow TIP points
    // to at min (current_y = height) and at max (current_y = 0). The tip is
    // y2 + width/2 = current_y - width/2, so max sits ABOVE the element top
    // (negative y) -- the svg is overflow:visible for exactly that reason.
    // Mirrors the C++ triangle_value_tiger_widget::draw_triangle_widget.
    const minTipY = Math.round((height - Math.round(width / 2)) * scale);
    const maxTipY = Math.round((0 - Math.round(width / 2)) * scale);
    const markX2 = Math.round(width * scale);
    // +0.5 centres the 1px stroke on the tip's pixel row; on an integer y
    // it snaps to the row above (measured).
    const mark = (y: number) =>
        `<line x1="0" y1="${y + 0.5}" x2="${markX2}" y2="${y + 0.5}" stroke="black" stroke-width="1" shape-rendering="crispEdges"/>`;

    var triangle_svg : string = `<svg style="overflow:visible" width="${svgW}px" height="${svgH}px">`;
    triangle_svg += mark(minTipY) + mark(maxTipY);
    triangle_svg += "<polygon points=\""+ points + "\"";
    // shape-rendering=crispEdges: an SVG stroke is CENTRED on the path, so a
    // stroke-width of 1 on an integer coordinate straddles the pixel boundary
    // and antialiases into TWO half-covered columns -- a 2px smudge. Disabling
    // antialiasing snaps it to a single black pixel, which is also how the C++
    // draws it (draw_polygone, 1px GDI pen, no AA).
    triangle_svg += " fill='" + itemValue.c +"' stroke='black' stroke-width='1' shape-rendering='crispEdges'/>";
    triangle_svg += "</svg>";

    el.innerHTML = triangle_svg;

    TOOLTIP( el, tag_name + ' ' + itemValue.t + description );
}

// `LINE` — ported from C++ `line_tiger_shape_widget::draw_line_shape_widget`.
// `func_params` (pipe-separated, mirrors the TRIANGLE arm) carries the line's
// two endpoints local to the widget's bounding box plus the stroke width:
// `lx1|ly1|lx2|ly2|lineWidth|tagName|description` — set by the `"LINE"` arm
// in `tiger_view_widgets.rs`.
function LINE(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
    const el = document.getElementById(tiger_el.id);
    if (!el) return;

    const params = (tiger_el.func_params ?? "").split('|');
    const x1 : number = parseFloat(params[0]);
    const y1 : number = parseFloat(params[1]);
    const x2 : number = parseFloat(params[2]);
    const y2 : number = parseFloat(params[3]);
    const lineWidth : number = parseFloat(params[4]) || 1;

    const tag_name : string = params[5] ?? "";
    const description : string = params[6] ?? "";

    const sx1 = Math.round(x1 * scale);
    const sy1 = Math.round(y1 * scale);
    const sx2 = Math.round(x2 * scale);
    const sy2 = Math.round(y2 * scale);
    const sw = Math.max(1, Math.round(lineWidth * scale));

    const svgW = Math.max(sx1, sx2) + sw;
    const svgH = Math.max(sy1, sy2) + sw;

    // An empty colour mirrors the C++ "erase" branch (`channel_value == 0 &&
    // off_colour_str.empty()`): a digital-off line with no configured off
    // colour disappears entirely rather than drawing black.
    const strokeColour = itemValue.c === "" ? "none" : itemValue.c;

    let lineEl = el.querySelector<SVGLineElement>('line');

    if (!lineEl || el.dataset.scale !== String(scale)) {
        while (el.firstChild) el.removeChild(el.firstChild);
        var svg : string = `<svg style="overflow:visible" width="${svgW}px" height="${svgH}px">`;
        svg += `<line x1="${sx1}" y1="${sy1}" x2="${sx2}" y2="${sy2}" stroke="${strokeColour}" stroke-width="${sw}"/>`;
        svg += `</svg>`;
        el.innerHTML = svg;
        el.dataset.scale = String(scale);
    } else {
        lineEl.setAttribute('stroke', strokeColour);
    }

    TOOLTIP( el, tag_name + ' ' + itemValue.t + description );
}

function GENERATOR_CHART(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    while (el.firstChild)
        el.removeChild(el.firstChild);

   const params = (tiger_el.func_params ?? "").split('|');
   const width : number = parseFloat(params[0]);
   const height : number = parseFloat(params[1]);
   const min_x : number = parseFloat(params[2]);
   const max_x : number = parseFloat(params[3]);
   const min_y : number = parseFloat(params[4]);
   const max_y : number = parseFloat(params[5]);

   	var current_value_x = itemValue.v[0];
   	var current_value_y = itemValue.v[1];

    var scaledW = Math.round(width * scale);
    var scaledH = Math.round(height * scale);

    var x_line_str = "";
	if (current_value_x < min_x) {
        x_line_str = `<line x1="0" y1="0" x2="0" y2="${scaledH}" stroke="${itemValue.c}" stroke-width="1" />`;
    }
    else if ( current_value_x > max_x ) {
        x_line_str = `<line x1="${scaledW}" y1="0" x2="${scaledW}" y2="${scaledH}" stroke="${itemValue.c}" stroke-width="1" />`;
    }
    else {
        var current_x = Math.round( (( ( current_value_x - min_x ) / ( max_x - min_x ) ) * width ) * scale);
        x_line_str = `<line x1="${current_x}" y1="0" x2="${current_x}" y2="${scaledH}" stroke="${itemValue.c}" stroke-width="1" />`;
    }

    var y_line_str = "";
    if ( current_value_y < min_y ) {
        y_line_str = `<line x1="0" y1="${scaledH}" x2="${scaledW}" y2="${scaledH}" stroke="${itemValue.c}" stroke-width="1" />`;
    }
    else if ( current_value_y > max_y ) {
        y_line_str = `<line x1="0" y1="0" x2="${scaledW}" y2="0" stroke="${itemValue.c}" stroke-width="1" />`;
    }
    else {
        var current_y = Math.round((height - Math.round( ( ( current_value_y - min_y ) / ( max_y - min_y ) ) * height )) * scale);
        y_line_str = `<line x1="0" y1="${current_y}" x2="${scaledW}" y2="${current_y}" stroke="${itemValue.c}" stroke-width="1" />`;
    }

    var generator_svg : string = `<svg style="overflow:visible" width="${width *scale}px" height="${height * scale}px">`;
    generator_svg += x_line_str;
    generator_svg += y_line_str;
    generator_svg += "</svg>";

    el.innerHTML = generator_svg;
}

function calc_polar_plot_scale(min_allowed_range: number, min_value: number, max_value: number )
    {
    var min = min_value;
    var max = max_value;

	if (min > max) {
    min = max_value;
    max = min_value;
    }

	var range = max - min;

    var largest = Math.abs( max );
    if (largest < Math.abs( min ) ){
    largest = Math.abs( min );
    }

	var adjust_value = (largest * 0.00005);

	if ( 0.0 == adjust_value) {
    adjust_value = 1.0;

		if ( ( adjust_value * 2.0 ) < min_allowed_range) {
    adjust_value  = min_allowed_range / 2.0;
    }
    }

	if (range < (adjust_value * 2.0)) {
    max += adjust_value;
    min -= adjust_value;
    range = max - min;
    }

	var log_of_range = Math.log( Math.abs(range) ) / Math.log( 10 );
    log_of_range = Math.floor( log_of_range );

	var range_power_of_ten = Math.pow( 10, log_of_range);

	var min_num_powers_of_ten = Math.floor( min / range_power_of_ten );
    var max_num_powers_of_ten = Math.ceil( max / range_power_of_ten );

	min = Math.floor ( min_num_powers_of_ten ) * range_power_of_ten;
    max = Math.ceil( max_num_powers_of_ten ) * range_power_of_ten;

	range = max - min;

	if (range < min_allowed_range) {
    min = max - min_allowed_range;
    }

    return { center : min, edge : max };
}

function draw_polar_plot_spoke(plot_colour: string, radius: number, angle: number, min_value: number, max_value: number, current_value: number, last_x: number, last_y: number )
{
    const spoke_angle_cos = Math.cos( angle );
    const spoke_angle_sin = Math.sin( angle );

    const x2= radius + Math.round(radius * spoke_angle_cos);
    const y2= radius + Math.round(radius * spoke_angle_sin);

    var spoke_str = `<line x1="${radius}" y1="${radius}" x2="${x2}" y2="${y2}" stroke="#bebebe" stroke-width="1" stroke-dasharray="2,2"/>`;

     // draw the spoke points
    var spoke_range = (max_value - min_value);

    var spoke_value_to_distance_ratio = radius / spoke_range;

    if (isNaN(current_value)) {
        value_x = NaN;
        value_y = NaN;

        return { value_x: value_x, value_y: value_y };
    }

	var spoke_distance = spoke_value_to_distance_ratio * ( current_value - min_value );

    var value_x = radius + ( spoke_distance * spoke_angle_cos );
    // Same sign as the spoke guide line above (SVG y grows downwards), so
    // spoke 0 (angle -90deg) sits at 12 o'clock and the rest run clockwise
    // with the rim labels, as in the C++ view.
    var value_y = radius + (spoke_distance * spoke_angle_sin);
    value_x = Math.round(value_x);
    value_y = Math.round(value_y);

    spoke_str += `<circle cx="${value_x}" cy="${value_y}" r="2" fill="${plot_colour}"/>`;

    if ( !isNaN( last_x ) && !isNaN( last_y ) ) {
        spoke_str += `<line x1="${last_x}" y1="${last_y}" x2="${value_x}" y2="${value_y}" stroke="${plot_colour}" stroke-width="1" />`;
    }

    return { spoke_str: spoke_str, value_x : value_x, value_y : value_y };
}


function draw_last_polar_plot_line( plot_colour:string, first_x:number, first_y:number, last_x:number, last_y:number )
{
    var last_line : string = "";
    if ( !isNaN( first_x ) && !isNaN( first_y ) && !isNaN( last_x ) && !isNaN( last_y ) ) {
        last_line += `<line x1="${last_x}" y1="${last_y}" x2="${first_x}" y2="${first_y}" stroke="${plot_colour}" stroke-width="1" />`;
   }
    return last_line;
}

function draw_polar_plot_circles( radius:number, number_of_circles: number, background_colour: string, axes_colour: string)
{
    var svg_circle = `<circle cx="${radius}" cy="${radius}" r="${radius}" fill="${background_colour}"/>`;

    for (var circle_index = 0; circle_index < number_of_circles; circle_index++) {
        var current_radius = Math.round(((circle_index + 1) / (number_of_circles + 1)) * radius);
        svg_circle += `<circle cx="${radius}" cy="${radius}" r="${current_radius}" fill="none" stroke="${axes_colour}" stroke-width="1" stroke-dasharray="2,2"/>`;
    }

    return svg_circle;
}

function POLAR_PLOT(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    const params = (tiger_el.func_params ?? "").split('|');
    const diameter : number = parseFloat(params[0]);
    const number_of_spokes : number = parseFloat(params[1]);
    const number_of_circles : number = parseFloat(params[2]);
    const min_autoscale_range : number = parseFloat(params[3]);
    const background_colour : string = params[4];
    const axes_colour : string = params[5];
    const plot_colour : string = params[6];

    var min_value : number = NaN;
    var max_value : number = NaN;

    for ( var array_index : number = 0; array_index < itemValue.v.length; array_index++ ) {
        if ( !isNaN(  itemValue.v[array_index] ) && itemValue.v[array_index] != INVALID_TIGER_VALUE) {
            if ( isNaN( min_value ) ||  itemValue.v[array_index] < min_value ) {
                min_value =  itemValue.v[array_index];
            }
            if ( isNaN( max_value ) ||  itemValue.v[array_index] > max_value ) {
                max_value =  itemValue.v[array_index];
            }
        }
    }

    var plot_scale = calc_polar_plot_scale( min_autoscale_range, min_value, max_value );

    var radius = Math.round( (diameter / 2 ) *scale );

    const polar_plot_circles = draw_polar_plot_circles(radius, number_of_circles, background_colour, axes_colour);

    var last_x = NaN;
    var last_y = NaN;
    var first_x = NaN;
    var first_y = NaN;

    var spoke_str : string = "";

    for( var spoke_index : number = 0; spoke_index < number_of_spokes; spoke_index++ ) {
        const angle = ((spoke_index / number_of_spokes) * (Math.PI * 2)) - (Math.PI / 2);
        var res = draw_polar_plot_spoke( plot_colour, radius, angle, plot_scale.center, plot_scale.edge, itemValue.v[spoke_index], last_x, last_y);

        spoke_str += res.spoke_str;

        if ( spoke_index == 0 ) {
            first_x = res.value_x;
            first_y = res.value_y;
        }

        last_x = res.value_x;
        last_y = res.value_y;
    }

    spoke_str += draw_last_polar_plot_line ( plot_colour, first_x, first_y, last_x, last_y );

    var polar_plot_svg : string = `<svg style="overflow:visible" width="${diameter *scale}px" height="${diameter * scale}px">`;
    polar_plot_svg += polar_plot_circles;
    polar_plot_svg += spoke_str;
    polar_plot_svg += "</svg>";

    el.innerHTML = polar_plot_svg;
}

function draw_nyquist_spokes(radius: number, axes_colour: string)
{
    var spoke_str: string = "";

    spoke_str += `<line x1="${radius}" y1="${radius}" x2="${radius + radius}" y2="${radius}" stroke="${axes_colour}" stroke-width="1"/>`;
    spoke_str += `<line x1="${radius}" y1="${radius}" x2="${radius}" y2="0" stroke="${axes_colour}" stroke-width="1"/>`;
    spoke_str += `<line x1="${radius}" y1="${radius}" x2="0" y2="${radius}" stroke="${axes_colour}" stroke-width="1"/>`;
    spoke_str += `<line x1="${radius}" y1="${radius}" x2="${radius}" y2="${radius + radius}" stroke="${axes_colour}" stroke-width="1"/>`;

    return spoke_str;
 }

 function nyquist_scale(max_value: number)
{
    var log_of_range = Math.log(Math.abs(max_value)) / Math.log(10);
    log_of_range = Math.floor(log_of_range);

    var range_power_of_ten = Math.pow( 10, log_of_range);

    var max_num_powers_of_ten = Math.ceil(max_value / range_power_of_ten);

    return Math.ceil(max_num_powers_of_ten) * range_power_of_ten;
}

function draw_nyqsuit_vector(plot_colour:string, radius :number, range:number, amplitude:number , angle:number)
{
    var vector_str: string = "";

    var x = Math.round(radius + (Math.cos(angle) * ((amplitude * radius) / range)));
    var y = Math.round(radius - (Math.sin(angle) * ((amplitude * radius) / range)));

    vector_str += `<line x1="${radius}" y1="${radius}" x2="${x}" y2="${y}" stroke="${plot_colour}" stroke-width="2"/>`;

    var arrow_angle : number = (Math.PI / 4.0) - angle;

    var h1x = x - Math.round(5 * Math.cos(arrow_angle));
    var h1y = y - Math.round(5 * Math.sin(arrow_angle));

    vector_str += `<line x1="${x}" y1="${y}" x2="${h1x}" y2="${h1y}" stroke="${plot_colour}" stroke-width="2"/>`;

    arrow_angle = (Math.PI / 4.0) + angle;

    var h2x = x - Math.round(5 * Math.cos(arrow_angle));
    var h2y = y + Math.round(5 * Math.sin(arrow_angle));
    vector_str += `<line x1="${x}" y1="${y}" x2="${h2x}" y2="${h2y}" stroke="${plot_colour}" stroke-width="2"/>`;

    return vector_str;
}

function NYQUIST_PLOT(tiger_el : TigerViewElement, itemValue: ItemValue, scale: number)
{
   const el = document.getElementById(tiger_el.id);
   if (!el) return;

    const params = (tiger_el.func_params ?? "").split('|');
    const diameter : number = parseFloat(params[0]);
    const number_of_vectors : number = parseFloat(params[1]);
    const number_of_circles : number = parseFloat(params[2]);
    const background_colour : string = params[3];
    const axes_colour : string = params[4];
    const vectors_colour_list : string = params[5];
    const vector_colour = (vectors_colour_list ?? "").split(',');

    var item_amp_value = new Array();
    var item_phase_value = new Array();


    for ( var vector_index : number = 0; vector_index <number_of_vectors; vector_index++ ) {
        item_amp_value[vector_index] = itemValue.v[vector_index * 2];
        item_phase_value[vector_index] = itemValue.v[(vector_index * 2) +1];
    }

    var radius = Math.round( (diameter / 2 ) *scale );

    const polar_plot_circles_svg = draw_polar_plot_circles(radius, number_of_circles, background_colour, axes_colour);
    const nyquist_spokes_svg = draw_nyquist_spokes(radius, axes_colour);

        // Calculate the max
    var max_value :number = NaN;
    for (var vector_index : number = 0; vector_index < number_of_vectors; vector_index++) {
        if (!isNaN(item_amp_value[vector_index])) {
            if (isNaN(max_value) || item_amp_value[vector_index] > max_value) {
                max_value = item_amp_value[vector_index];
            }
        }
    }

    const ny_scale :number = nyquist_scale(max_value);

    var vector_svg : string = "";
    for (var vector_index : number = 0; vector_index < number_of_vectors; vector_index++) {
        vector_svg += draw_nyqsuit_vector(vector_colour[vector_index], radius, ny_scale, item_amp_value[vector_index], item_phase_value[vector_index]);
    }

    var nyquist_plot_svg : string = `<svg style="overflow:visible" width="${diameter *scale}px" height="${diameter * scale}px">`;
    nyquist_plot_svg += polar_plot_circles_svg;
    nyquist_plot_svg += nyquist_spokes_svg;
    nyquist_plot_svg += vector_svg;
    nyquist_plot_svg += "</svg>";

    el.innerHTML = nyquist_plot_svg;
}

const graphInstances = new Map<string, uPlot>();

// Per-graph cache of the most recently rendered series data, indexed by plot
// position. Used by update_tiger_graph to merge incoming partial updates: when
// a new event arrives with an empty series (no samples yet from the backend),
// we keep the previously-rendered data for that plot instead of wiping it.
const graphSeriesCache = new Map<string, GraphSeriesData[]>();

// Per-graph last-valid (x,y) in data coordinates, one entry per XY series.
// `null` entries mean "don't draw a last-value cross for this series" (either
// the plot ini didn't set show_last_value, or the series has no valid samples
// yet). Re-populated on every update_tiger_graph_xy; read by the uPlot `draw`
// hook on every render/resize so fast-path setData keeps the overlay fresh.
const xyLastPointOverlay = new Map<string, Array<{ x: number; y: number } | null>>();

// IDs whose tracked uPlot is currently a placeholder empty frame (from
// render_empty_graph). The real sub-functions must recreate rather than take
// the setData fast path on these — otherwise the transparent-placeholder
// series config persists and real data renders invisible.
const emptyGraphFrames = new Set<string>();

/** Clear the cached series data for a single graph (or all graphs if no id). */
export function clear_graph_series_cache(graphId?: string) {
    if (graphId === undefined) graphSeriesCache.clear();
    else graphSeriesCache.delete(graphId);
}

/** Visually clear a GRAPH widget: drop its cached series and repaint it as an
 *  empty frame (axes/grid, no data). Used on a Live/Replay mode switch so the
 *  outgoing mode's plot is wiped immediately, before the new mode's data is
 *  requested -- otherwise the stale plot lingers on screen until the refetch
 *  returns (or, if the new mode has no data yet, indefinitely). */
export function clear_tiger_graph(tiger_el: TigerViewElement, scale: number, hideAxes = false) {
    graphSeriesCache.delete(tiger_el.id);
    const el = document.getElementById(tiger_el.id);
    if (!el) return;
    render_empty_graph(tiger_el, el, scale, hideAxes);
}

/** Destroy any tracked uPlot instances whose ids aren't in `keepIds`, and drop
 *  their cached series. Used on view switch so stale graphs from the previous
 *  screen don't keep state that would bleed into the next one. */
export function prune_tiger_graphs(keepIds: Set<string>) {
    for (const id of Array.from(graphInstances.keys())) {
        if (keepIds.has(id)) continue;
        const inst = graphInstances.get(id);
        try { inst?.destroy(); } catch {}
        graphInstances.delete(id);
        graphSeriesCache.delete(id);
        xyLastPointOverlay.delete(id);
        emptyGraphFrames.delete(id);
    }
    for (const id of Array.from(graphSeriesCache.keys())) {
        if (!keepIds.has(id)) graphSeriesCache.delete(id);
    }
    for (const id of Array.from(xyLastPointOverlay.keys())) {
        if (!keepIds.has(id)) xyLastPointOverlay.delete(id);
    }
}

/** Synchronously resize a graph's uPlot canvas to match the given CSS-pixel size. */
export function resize_tiger_graph(graphId: string, width: number, height: number) {
    const instance = graphInstances.get(graphId);
    const el = document.getElementById(graphId);
    const hasUplot = !!(el && el.querySelector(".uplot"));
    console.log(`resize_tiger_graph id=${graphId} w=${width} h=${height} hasInstance=${!!instance} hasUplot=${hasUplot}`);
    if (!instance || !hasUplot) return;
    instance.setSize({ width, height });
}

function GRAPH(tiger_el : TigerViewElement, _itemValue: ItemValue, scale: number)
{
    const el = document.getElementById(tiger_el.id);
    if (!el) return;

    const scaledW = Math.round((tiger_el.width  ?? 300) * scale);
    const scaledH = Math.round((tiger_el.height ?? 100) * scale);

    // Resize the existing uPlot chart to match the current scale. If no plot
    // exists yet (no graph-update event has arrived), render an empty frame so
    // the widget is visible as a placeholder instead of a blank div.
    const existing = graphInstances.get(tiger_el.id);
    if (existing && el.querySelector(".uplot")) {
        existing.setSize({ width: scaledW, height: scaledH });
    } else {
        render_empty_graph(tiger_el, el, scale);
    }
}

export function update_tiger_widgets(tiger_el : TigerViewElement, up: ItemValue, ox: number, oy: number, scale: number = 1)
{
//    console.log("update_tiger_widgets id:"+  tiger_el.id + " func " + tiger_el.tiger_func);

    switch(tiger_el.tiger_func) {
        case 'TEXT': TEXT(tiger_el, up); break;
        case 'TEXT_COLOUR': TEXT_COLOUR(tiger_el, up); break;
        case 'STATUS': STATUS(tiger_el, up, ox, scale); break;
        case 'ANALOG': ANALOG(tiger_el, up); break;
		case 'MAINTENANCE_TEXT': MAINTENANCE_TEXT(tiger_el, up); break;
		case 'MAINTENANCE_DATE': MAINTENANCE_DATE(tiger_el, up); break;
		case 'ANALOG_BOX': ANALOG_BOX(tiger_el, up); break;
		case 'DIGITAL_BOX': DIGITAL_BOX(tiger_el, up); break;
		case 'DIGITAL': DIGITAL(tiger_el, up); break;
		case 'DIGITALSTATUS': DIGITALSTATUS(tiger_el, up); break;
		case 'DIGITALTEXT': DIGITALTEXT(tiger_el, up); break;
		case 'ALARM_CONNECTION_BOX': ALARM_CONNECTION_BOX(tiger_el, up); break;
		case 'RECTANGLE': RECTANGLE(tiger_el, up); break;		
        case 'BARCHART': BARCHART(tiger_el, up, oy, scale); break;
        case 'HORIZ_BARCHART': HORIZ_BARCHART(tiger_el, up, ox, scale); break;
        case 'STACK_BARCHART': STACK_BARCHART(tiger_el, up, oy, scale); break;
        case 'EXHAUST_SPREAD': EXHAUST_SPREAD(tiger_el, up, ox, scale); break;
        case 'DIGITAL_BITMAP': DIGITAL_BITMAP(tiger_el, up, scale); break;
        case 'CIRCLE': CIRCLE(tiger_el, up, scale); break;
        case 'EXHAUST_PATTERN': EXHAUST_PATTERN(tiger_el, up, scale); break;
        case 'EXHAUST_ARC': EXHAUST_ARC(tiger_el, up, scale); break;
        case 'FLAME_INTENSITY': FLAME_INTENSITY(tiger_el, up, scale); break;
        case 'TRIANGLE': TRIANGLE(tiger_el, up, scale); break;
        case 'TRIANGLE_VALUE': TRIANGLE_VALUE(tiger_el, up, scale); break;
        case 'LINE': LINE(tiger_el, up, scale); break;
        // CENTERLINE has no `WidgetEntry` (see the `"CENTERLINE"` arm comment in
        // tiger_view_widgets.rs) — it's a static bordered placeholder rendered
        // entirely from its initial `class_name`/`text`/`extra_style`, so there's
        // nothing to update per tick.
        case 'CENTERLINE': break;
        case 'GENERATOR_CHART': GENERATOR_CHART(tiger_el, up, scale); break;
        case 'POLAR_PLOT': POLAR_PLOT(tiger_el, up, scale); break;
        case 'NYQUIST_PLOT': NYQUIST_PLOT(tiger_el, up, scale); break;
        case 'GRAPH': GRAPH(tiger_el, up, scale); break;

        default:
        console.log("tiger func " + tiger_el.tiger_func + " not supported");
        break;
    }
}

// Fallback colour palette when INI doesn't specify one
const GRAPH_FALLBACK_COLOURS = ["lime", "yellow", "cyan", "orange", "magenta", "#ff6666", "#66ff66", "#6666ff"];

const GRAPH_Y_AXIS_OPTS: uPlot.Axis = {
    stroke: "black",
    grid: { stroke: "black", width: 1 },
    ticks: { stroke: "black" },
    font: "10px monospace",
    size: 40,
};

// Thumbnail graph axes (dynamic DYN_*_GRAPH views): keep the grid but drop the
// numeric scale values, tick marks, and label gutter so the mini-plot fills the
// cell. `values` returns blank labels for every split.
const blankAxisValues = (_u: uPlot, splits: number[]) => splits.map(() => "");
const THUMBNAIL_X_AXIS: uPlot.Axis = {
    stroke: "black",
    grid: { stroke: "black", width: 1 },
    ticks: { show: false },
    size: 2,
    gap: 0,
    values: blankAxisValues,
};
const THUMBNAIL_Y_AXIS: uPlot.Axis = {
    stroke: "black",
    grid: { stroke: "black", width: 1 },
    ticks: { show: false },
    size: 2,
    gap: 0,
    values: blankAxisValues,
};

/** Render an empty graph frame — axes and grid, no data. Used when a widget
 *  is first mounted before any graph-update has arrived, or when a graph
 *  update carries no data (and no cached data exists) to replace a blank div
 *  with a visible placeholder. */
function render_empty_graph(tiger_el: TigerViewElement, el: HTMLElement, scale: number, hideAxes = false) {
    const scaledW = Math.round((tiger_el.width ?? 300) * scale);
    const scaledH = Math.round((tiger_el.height ?? 100) * scale);

    // Keep the existing empty frame alive across redundant calls (e.g. a
    // refresh tick that still has no data).
    const existing = graphInstances.get(tiger_el.id);
    if (existing && el.querySelector(".uplot") && emptyGraphFrames.has(tiger_el.id)) {
        existing.setSize({ width: scaledW, height: scaledH });
        return;
    }

    if (existing) {
        existing.destroy();
        graphInstances.delete(tiger_el.id);
    }
    xyLastPointOverlay.delete(tiger_el.id);
    while (el.firstChild) el.removeChild(el.firstChild);

    const data: uPlot.AlignedData = [[0, 1], [null, null]];
    const opts: uPlot.Options = {
        width: scaledW,
        height: scaledH,
        series: [{}, { stroke: "transparent" }],
        axes: hideAxes
            ? [THUMBNAIL_X_AXIS, THUMBNAIL_Y_AXIS]
            : [
                { stroke: "black", grid: { stroke: "black", width: 1 }, ticks: { stroke: "black" }, font: "10px monospace", size: 30 },
                GRAPH_Y_AXIS_OPTS,
            ],
        scales: { x: { time: false, range: [0, 1] }, y: { range: [0, 1] } },
        legend: { show: false },
        cursor: { show: false },
    };
    graphInstances.set(tiger_el.id, new uPlot(opts, data, el));
    emptyGraphFrames.add(tiger_el.id);
}

/** Merge an incoming series list with the previously rendered series for this
 *  graph. For each plot position, keep the cached one when the incoming one is
 *  empty — prevents a refresh cycle (or still-pending group in the current
 *  cycle) from wiping plots that already have data on screen. `hasData` tells
 *  the merge which field signals "populated" (timestamps for time, x_values
 *  for XY). */
export function merge_graph_series(
    graphId: string,
    incoming: GraphSeriesData[],
    hasData: (s: GraphSeriesData) => boolean,
): GraphSeriesData[] {
    const cached = graphSeriesCache.get(graphId) ?? [];
    const mergedLen = Math.max(incoming.length, cached.length);
    const series: GraphSeriesData[] = new Array(mergedLen);
    for (let i = 0; i < mergedLen; i++) {
        const inc = incoming[i];
        const prev = cached[i];
        if (inc && hasData(inc)) series[i] = inc;
        else if (prev) series[i] = prev;
        else series[i] = inc ?? { label: "", color: "", timestamps: [], values: [] };
    }
    graphSeriesCache.set(graphId, series);
    return series;
}

/** Update a GRAPH widget with real data from the backend. Dispatches to the
 *  time-series or XY implementation based on whether any incoming (or cached)
 *  series carries `x_values`. */
export function update_tiger_graph(tiger_el: TigerViewElement, gv: GraphValue, _ox: number, _oy: number, scale: number, hideAxes = false) {
    const el = document.getElementById(tiger_el.id);
    if (!el) return;

    const incoming = gv.m_graph_data;
    // Fall back to the cache so a refresh tick with empty incoming still
    // routes to the right mode — otherwise an XY plot with a slow update
    // would briefly flip to time-series and back.
    const cached = graphSeriesCache.get(tiger_el.id) ?? [];
    const isXY = incoming.some((s) => s.x_values != null)
              || cached.some((s) => s.x_values != null);

    if (isXY) {
        update_tiger_graph_xy(tiger_el, el, incoming, scale, hideAxes);
    } else {
        update_tiger_graph_timeseries(tiger_el, el, incoming, scale, hideAxes);
    }
}

/**
 * Rebase a list of multi-time series so they share an elapsed-seconds X axis.
 * CURRENT plots (Unix-origin timestamps) are subtracted to start at 0.
 * RELATIVE/HISTORICAL plots (already event-relative) are passed through.
 *
 * `parsed.plots[i]` is matched to `series[i]` by index — both originate from
 * the same `plot[i]` ordering in the backend params string.
 *
 * Falls back to the legacy "subtract t0 per series" behaviour when `parsed`
 * is undefined (e.g. graph_tags_param not yet plumbed through).
 */
export function rebaseMultiTimeSeries(
    series: GraphSeriesData[],
    parsed: ParsedGraphParams | undefined,
): GraphSeriesData[] {
    return series.map((s, i) => {
        if (s.timestamps.length === 0) return s;

        if (!parsed) {
            // Legacy fallback: subtract t0 from each series' own timestamps.
            const t0 = s.timestamps[0];
            return { ...s, timestamps: s.timestamps.map((t) => t - t0) };
        }

        const mode = parseMultiTimeMode(parsed.plots[i]?.multitimeMode);
        if (mode === "CURRENT") {
            const firstUnix = s.timestamps[0];
            return { ...s, timestamps: s.timestamps.map((t) => t - firstUnix) };
        }
        // RELATIVE / HISTORICAL: timestamps are already event-relative seconds; do
        // not rebase (preserves negative pre-event samples).
        return s;
    });
}

/** Render a time-series graph (x-axis = timestamps). */
function update_tiger_graph_timeseries(
    tiger_el: TigerViewElement,
    el: HTMLElement,
    incoming: GraphSeriesData[],
    scale: number,
    hideAxes = false,
) {
    const rawSeries = merge_graph_series(tiger_el.id, incoming, (s) => s.timestamps.length > 0);

    // Nothing to plot (and nothing cached) — show an empty frame.
    if (!rawSeries.some((s) => s.timestamps.length > 0)) {
        render_empty_graph(tiger_el, el, scale, hideAxes);
        return;
    }

    // Multi-time graphs overlay distinct historical / live windows; each series
    // arrives on its own absolute timeline (RELATIVE plots in event-start
    // seconds, CURRENT plots in Unix seconds). Rebase CURRENT plots to elapsed
    // seconds; pass RELATIVE/HISTORICAL through as-is (their timestamps are
    // already event-relative and may include negative pre-event samples).
    const isMultiTime = tiger_el.graph_mode === "multiTime";
    // Event graphs draw one continuous line with no gaps (mirrors GraphWindow /
    // report event graphs): skip the time-gap line breaks and span nulls.
    const isEventMode = tiger_el.graph_mode === "event";
    const parsedParams = isMultiTime && tiger_el.graph_tags_param
        ? parseGraphParams(tiger_el.graph_tags_param)
        : undefined;
    const series: GraphSeriesData[] = isMultiTime
        ? rebaseMultiTimeSeries(rawSeries, parsedParams)
        : rawSeries;

    const scaledW = Math.round((tiger_el.width ?? 300) * scale);
    const scaledH = Math.round((tiger_el.height ?? 100) * scale);

    // Shared x axis as the sorted union of every series' timestamps. Handles
    // multi-time graphs where each plot has its own event range; for normal
    // graphs with shared timestamps the union equals that shared array.
    const xSet = new Set<number>();
    for (const s of series) for (const x of s.timestamps) xSet.add(x);
    const xAxisValues = Array.from(xSet).sort((a, b) => a - b);
    const xIndex = new Map<number, number>();
    for (let i = 0; i < xAxisValues.length; i++) xIndex.set(xAxisValues[i], i);

    const alignedValues: (number | null)[][] = series.map((s) => {
        const out: (number | null)[] = new Array(xAxisValues.length).fill(null);
        for (let i = 0; i < s.timestamps.length; i++) {
            const idx = xIndex.get(s.timestamps[i]);
            if (idx === undefined) continue;
            const v = s.values[i];
            out[idx] = (!Number.isFinite(v) || v >= INVALID_TIGER_VALUE) ? null : v;
        }
        return out;
    });

    // Break the line across large time gaps, mirroring C++
    // tiger_plot::add_to_data_buffers: insert one null sample at
    // prev_time + period whenever two consecutive samples are more than 1.95
    // periods apart, so uPlot renders a gap instead of a long line bridging
    // missing data. The period is the MEDIAN actual spacing (the real cadence);
    // the message sample_rate can be far smaller than the true spacing and would
    // break the whole line into invisible isolated points. Absolute axis only
    // (multi-time series are event-aligned, no wall clock).
    let plotX = xAxisValues;
    let plotY = alignedValues;
    let period = 0;
    if (!isMultiTime && !isEventMode && xAxisValues.length > 2) {
        const gapDiffs: number[] = [];
        for (let i = 1; i < xAxisValues.length; i++) gapDiffs.push(xAxisValues[i] - xAxisValues[i - 1]);
        period = gapDiffs.slice().sort((a, b) => a - b)[Math.floor(gapDiffs.length / 2)];
    }
    if (!isMultiTime && !isEventMode && period > 0 && xAxisValues.length > 1) {
        const threshold = 1.95 * period;
        const nx: number[] = [];
        const ny: (number | null)[][] = alignedValues.map(() => []);
        for (let i = 0; i < xAxisValues.length; i++) {
            if (i > 0 && xAxisValues[i] - xAxisValues[i - 1] > threshold) {
                nx.push(xAxisValues[i - 1] + period);
                for (const col of ny) col.push(null);
            }
            nx.push(xAxisValues[i]);
            for (let s = 0; s < alignedValues.length; s++) ny[s].push(alignedValues[s][i]);
        }
        plotX = nx;
        plotY = ny;
    }
    const data: uPlot.AlignedData = [plotX, ...plotY];

    const existing = graphInstances.get(tiger_el.id);
    // Fast path only when the series count matches AND the existing plot is a
    // real chart (not a placeholder empty frame, whose transparent-stroke
    // series config would persist through setData).
    const isEmptyFrame = emptyGraphFrames.has(tiger_el.id);
    if (!isEmptyFrame && existing && el.querySelector(".uplot") && existing.series.length - 1 === series.length) {
        existing.setSize({ width: scaledW, height: scaledH });
        existing.setData(data);
        return;
    }

    if (existing) {
        existing.destroy();
        graphInstances.delete(tiger_el.id);
    }
    while (el.firstChild) el.removeChild(el.firstChild);

    const seriesOpts: uPlot.Series[] = [
        {},
        ...series.map((s, i) => ({
            label: s.label,
            stroke: s.color || GRAPH_FALLBACK_COLOURS[i % GRAPH_FALLBACK_COLOURS.length],
            width: 1,
            ...(isEventMode ? { spanGaps: true } : {}),
        })),
    ];

    // Multi-time axes plot elapsed seconds; the label format scales with the
    // longest plot on the graph (mm:ss < 1 h, hh:mm:ss < 1 day, else Nd hh:mm).
    const multiTimeMaxDuration = isMultiTime ? maxPlotDurationSec(series) : 0;
    // The view ini's per-graph `x_label_type` (carried in func_params) selects
    // the tick format; `auto` resolves against the plotted span and event-scale
    // graphs drop labels, both per the C++ (tiger_graph_time.cpp:905-984).
    const xLabelType = resolveXLabelType(
        tiger_el.func_params,
        isEventMode,
        xAxisValues.length > 1 ? xAxisValues[xAxisValues.length - 1] - xAxisValues[0] : 0,
    );
    const xAxisOpts: uPlot.Axis = {
        stroke: "black",
        grid: { stroke: "black", width: 1 },
        ticks: { stroke: "black" },
        font: "10px monospace",
        // date_time ticks are two lines (HH:MM:SS over the date) — give the
        // axis enough gutter for the second line.
        size: !isMultiTime && xLabelType === "date_time" ? 44 : 30,
        // Minimum px between ticks: uPlot's 50px default is narrower than a
        // full date label, so long-range graphs drew overlapping dates.
        // Multi-time labels (`Nd hh:mm` at most) fit the time_only budget.
        space: xAxisTickSpacePx(isMultiTime ? "time_only" : xLabelType),
        values: (_u: uPlot, vals: number[]) => vals.map(v => {
            if (isMultiTime) {
                return formatMultiTimeAxisLabel(v, multiTimeMaxDuration);
            }
            return formatXAxisLabel(v, xLabelType);
        }),
    };

    const opts: uPlot.Options = {
        width: scaledW,
        height: scaledH,
        series: seriesOpts,
        axes: hideAxes ? [THUMBNAIL_X_AXIS, THUMBNAIL_Y_AXIS] : [xAxisOpts, GRAPH_Y_AXIS_OPTS],
        scales: { x: isMultiTime ? { time: false } : { time: true } },
        legend: { show: false },
        cursor: { show: false },
    };

    graphInstances.set(tiger_el.id, new uPlot(opts, data, el));
    emptyGraphFrames.delete(tiger_el.id);
}

/** XY_GRAPH view-widget render config, carried as JSON in the element's
 *  func_params (built by the backend from the Mviews.ini section — see
 *  build_xy_graph_widget_config): optional fixed axis ranges plus the
 *  limit_id1/limit_id2 polylines the C++ chart drew as extra data sets. */
interface XyWidgetConfig {
    x_min?: number;
    x_max?: number;
    y_min?: number;
    y_max?: number;
    limits?: { colour: string; points: [number, number][] }[];
}

function parseXyWidgetConfig(funcParams: string | null | undefined): XyWidgetConfig | null {
    if (!funcParams || !funcParams.startsWith("{")) return null;
    try {
        return JSON.parse(funcParams) as XyWidgetConfig;
    } catch {
        return null;
    }
}

/** Render an XY graph (x-axis = a channel's values, not time). */
function update_tiger_graph_xy(
    tiger_el: TigerViewElement,
    el: HTMLElement,
    incoming: GraphSeriesData[],
    scale: number,
    hideAxes = false,
) {
    const series = merge_graph_series(tiger_el.id, incoming, (s) => (s.x_values?.length ?? 0) > 0);
    const xyConfig = parseXyWidgetConfig(tiger_el.func_params);

    // Nothing to plot (and nothing cached) — show an empty frame.
    if (!series.some((s) => (s.x_values?.length ?? 0) > 0)) {
        render_empty_graph(tiger_el, el, scale, hideAxes);
        return;
    }

    const scaledW = Math.round((tiger_el.width ?? 300) * scale);
    const scaledH = Math.round((tiger_el.height ?? 100) * scale);

    // Shared x axis as the sorted union of every series' x_values.
    const xSet = new Set<number>();
    for (const s of series) for (const x of (s.x_values ?? [])) xSet.add(x);
    const xAxisValues = Array.from(xSet).sort((a, b) => a - b);
    const xIndex = new Map<number, number>();
    for (let i = 0; i < xAxisValues.length; i++) xIndex.set(xAxisValues[i], i);

    const alignedValues: (number | null)[][] = series.map((s) => {
        const out: (number | null)[] = new Array(xAxisValues.length).fill(null);
        const xs = s.x_values ?? [];
        for (let i = 0; i < xs.length; i++) {
            const idx = xIndex.get(xs[i]);
            if (idx === undefined) continue;
            const v = s.values[i];
            out[idx] = (!Number.isFinite(v) || v >= INVALID_TIGER_VALUE) ? null : v;
        }
        return out;
    });
    const data: uPlot.AlignedData = [xAxisValues, ...alignedValues];

    // Record last-valid (x,y) per series for the `show_last_value` crosshair
    // overlay. Done here so the refresh path (setData fast path below) also
    // picks up new last points on every update.
    const lastPoints: Array<{ x: number; y: number } | null> = series.map((s) => {
        if (!s.show_last_value) return null;
        const xs = s.x_values ?? [];
        for (let i = xs.length - 1; i >= 0; i--) {
            const x = xs[i];
            const y = s.values[i];
            if (Number.isFinite(x) && Number.isFinite(y) && y < INVALID_TIGER_VALUE) {
                return { x, y };
            }
        }
        return null;
    });
    xyLastPointOverlay.set(tiger_el.id, lastPoints);

    const existing = graphInstances.get(tiger_el.id);
    const isEmptyFrame = emptyGraphFrames.has(tiger_el.id);
    if (!isEmptyFrame && existing && el.querySelector(".uplot") && existing.series.length - 1 === series.length) {
        existing.setSize({ width: scaledW, height: scaledH });
        existing.setData(data);
        return;
    }

    if (existing) {
        existing.destroy();
        graphInstances.delete(tiger_el.id);
    }
    while (el.firstChild) el.removeChild(el.firstChild);

    const seriesOpts: uPlot.Series[] = [
        {},
        ...series.map((s, i) => {
            const colour = s.color || GRAPH_FALLBACK_COLOURS[i % GRAPH_FALLBACK_COLOURS.length];
            return {
                label: s.label,
                stroke: colour,
                fill: colour,
                width: 0,
                paths: () => null,
                points: { show: true, size: 4, stroke: colour, fill: colour },
            };
        }),
    ];

    const xAxisOpts: uPlot.Axis = {
        stroke: "black",
        grid: { stroke: "black", width: 1 },
        ticks: { stroke: "black" },
        font: "10px monospace",
        size: 30,
    };

    const graphId = tiger_el.id;
    // Fixed axis ranges from the ini's graph_x/y_min/max_value (C++
    // use_min_max_x/y); absent keys leave uPlot's auto-range in charge.
    const scales: uPlot.Scales = { x: { time: false } };
    if (xyConfig && xyConfig.x_min !== undefined && xyConfig.x_max !== undefined) {
        scales.x!.range = [xyConfig.x_min, xyConfig.x_max];
    }
    if (xyConfig && xyConfig.y_min !== undefined && xyConfig.y_max !== undefined) {
        scales.y = { range: [xyConfig.y_min, xyConfig.y_max] };
    }
    const limits = xyConfig?.limits ?? [];
    const opts: uPlot.Options = {
        width: scaledW,
        height: scaledH,
        series: seriesOpts,
        axes: hideAxes ? [THUMBNAIL_X_AXIS, THUMBNAIL_Y_AXIS] : [xAxisOpts, GRAPH_Y_AXIS_OPTS],
        scales,
        legend: { show: false },
        cursor: { show: false },
        hooks: {
            // Limit polylines (limit_id1/limit_id2), drawn like the C++ chart's
            // extra limit data sets: a solid 1px line through the configured
            // points, clipped to the plot area.
            draw: [(u: uPlot) => {
                if (limits.length === 0) return;
                const ctx = u.ctx;
                const { left, top, width: bw, height: bh } = u.bbox;
                ctx.save();
                ctx.beginPath();
                ctx.rect(left, top, bw, bh);
                ctx.clip();
                ctx.lineWidth = 1;
                ctx.setLineDash([]);
                for (const limit of limits) {
                    if (!limit.points?.length) continue;
                    ctx.strokeStyle = limit.colour || "red";
                    ctx.beginPath();
                    let started = false;
                    for (const [px, py] of limit.points) {
                        const xPix = u.valToPos(px, "x", true);
                        const yPix = u.valToPos(py, "y", true);
                        if (!Number.isFinite(xPix) || !Number.isFinite(yPix)) continue;
                        if (started) ctx.lineTo(xPix, yPix);
                        else { ctx.moveTo(xPix, yPix); started = true; }
                    }
                    ctx.stroke();
                }
                ctx.restore();
            },
            // Draw white full-width/height crosshairs at each series' last
            // valid sample when plot[i].show_last_value is set. Reads from
            // xyLastPointOverlay so updates-via-setData refresh the positions.
            (u: uPlot) => {
                const overlay = xyLastPointOverlay.get(graphId);
                if (!overlay) return;
                const ctx = u.ctx;
                const { left, top, width: bw, height: bh } = u.bbox;
                ctx.save();
                ctx.strokeStyle = "#ffffff";
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 4]);
                for (const p of overlay) {
                    if (!p) continue;
                    const xPix = u.valToPos(p.x, "x", true);
                    const yPix = u.valToPos(p.y, "y", true);
                    if (!Number.isFinite(xPix) || !Number.isFinite(yPix)) continue;
                    ctx.beginPath();
                    ctx.moveTo(left, yPix);
                    ctx.lineTo(left + bw, yPix);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(xPix, top);
                    ctx.lineTo(xPix, top + bh);
                    ctx.stroke();
                }
                ctx.restore();
            }],
        },
    };

    graphInstances.set(tiger_el.id, new uPlot(opts, data, el));
    emptyGraphFrames.delete(tiger_el.id);
}
